import { ReactNode, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Login } from '@/pages/Login'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import type { LegacyUserRoles } from '@/services/legacyUserRoleService'

interface ProtectedRouteProps {
  children: ReactNode
  requiredLegacyRole?: keyof LegacyUserRoles
}

export function ProtectedRoute({ children, requiredLegacyRole }: ProtectedRouteProps) {
  const { t: tc } = useTranslation('common')
  const {
    isAuthenticated,
    isLoading,
    user,
    legacyRoles,
    legacyRolesSid,
    isLegacyRolesLoading,
    checkAuth,
    fetchLegacyRoles,
  } = useAuthStore()
  const location = useLocation()

  // Check authentication on mount
  useEffect(() => {
    const initAuth = async () => {
      await checkAuth()
    }
    initAuth()
  }, [])

  useEffect(() => {
    if (requiredLegacyRole && isAuthenticated && user?.sid) {
      void fetchLegacyRoles()
    }
  }, [requiredLegacyRole, isAuthenticated, user?.sid, fetchLegacyRoles])

  // Store the current path when user is not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      localStorage.setItem('redirectAfterLogin', location.pathname + location.search)
    }
  }, [isAuthenticated, isLoading, location.pathname, location.search])

  const waitingForLegacyRoles = Boolean(
    requiredLegacyRole &&
    isAuthenticated &&
    user?.sid &&
    (isLegacyRolesLoading || legacyRolesSid !== user.sid),
  )

  if (isLoading || waitingForLegacyRoles) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-transparent border-t-primary-600 mx-auto"></div>
          <p className="mt-4 text-sm text-gray-600">{tc('loading')}</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Login />
  }

  if (requiredLegacyRole && !legacyRoles?.[requiredLegacyRole]) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}
